import { getButtonTranslations } from '@common/i18n/buttons';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFileUploadTranslations } from '@common/i18n/fileUpload';
import {
    LEGACY_TABLE_UPLOAD_FILE_COUNT,
    type TableFormColumn,
} from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { parseMultiSelectValue } from '@common/utils/intakeFieldFormatting';
import { serializeIntakeTableRows } from '@common/utils/intakeTableValue';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import CurrencyInput from '@/components/CurrencyInput';
import GooglePlacesAutocomplete from '@/components/GooglePlacesAutocomplete';
import PercentageInput from '@/components/PercentageInput';
import FormUploadField from './FormUploadField';
import {
    createEmptyTableRow,
    getFixedTableRowCount,
    isEmptyTableCellValue,
    parseTableRows,
    sortMultiSelectByOptions,
    stringifyMultiSelectValue,
    type TableFormRow,
} from './tableValues';

export interface TableFormComponentProps {
    name: string;
    columns: TableFormColumn[];
    value?: string;
    disabled?: boolean;
    readOnly?: boolean;
    isError?: boolean;
    selectedLawFirmId?: number | string;
    language?: TemplateFormLanguage;
    rowCount?: number | null;
    onValueChange?: (name: string, value: string) => void;
}

export default function TableFormComponent({
    name,
    columns,
    value,
    disabled = false,
    readOnly = false,
    isError = false,
    selectedLawFirmId,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
    rowCount,
    onValueChange,
}: TableFormComponentProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const fileUploadTranslations = getFileUploadTranslations(language);
    const buttonTranslations = getButtonTranslations(language);
    const canModifyRows = !readOnly && getFixedTableRowCount(rowCount) === undefined;

    const rows = useMemo(
        () => parseTableRows(value, columns, rowCount),
        [value, columns, rowCount],
    );

    const isCellError = useCallback(
        (rowIndex: number, column: TableFormColumn) => {
            if (!isError) {
                return false;
            }
            return isEmptyTableCellValue(rows[rowIndex]?.[column.key], column.type);
        },
        [isError, rows],
    );

    const updateValue = useCallback(
        (newRows: TableFormRow[]) => {
            if (onValueChange && !readOnly) {
                onValueChange(name, serializeIntakeTableRows(newRows));
            }
        },
        [name, onValueChange, readOnly],
    );

    const handleCellChange = useCallback(
        (rowIndex: number, columnKey: string, cellValue: string | string[]) => {
            if (readOnly) return;
            const newRows = [...rows];
            newRows[rowIndex] = { ...newRows[rowIndex], [columnKey]: cellValue };
            updateValue(newRows);
        },
        [rows, readOnly, updateValue],
    );

    const handleAddRow = useCallback(() => {
        if (readOnly || !canModifyRows) return;
        const newRows = [...rows, createEmptyTableRow(columns)];
        updateValue(newRows);
    }, [columns, rows, readOnly, canModifyRows, updateValue]);

    const handleDeleteRow = useCallback(
        (rowIndex: number) => {
            if (readOnly || !canModifyRows) return;
            if (rows.length <= 1) {
                updateValue([createEmptyTableRow(columns)]);
                return;
            }
            const newRows = rows.filter((_, i) => i !== rowIndex);
            updateValue(newRows);
        },
        [columns, rows, readOnly, canModifyRows, updateValue],
    );

    const renderCell = (
        column: TableFormColumn,
        rowIndex: number,
        cellValue: string | string[] | null | undefined,
    ) => {
        const stringValue = Array.isArray(cellValue) ? '' : cellValue || '';
        const cellError = isCellError(rowIndex, column);

        switch (column.type) {
            case 'text':
                return (
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={column.placeholder}
                        value={stringValue}
                        disabled={disabled}
                        error={cellError}
                        onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                            pointerEvents: readOnly ? 'none' : 'auto',
                        }}
                    />
                );

            case 'textarea':
                return (
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        placeholder={column.placeholder}
                        value={stringValue}
                        disabled={disabled}
                        error={cellError}
                        onChange={(e) => handleCellChange(rowIndex, column.key, e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                            pointerEvents: readOnly ? 'none' : 'auto',
                        }}
                    />
                );

            case 'select':
                return (
                    <FormControl fullWidth size="small" error={cellError}>
                        <Select
                            value={stringValue}
                            disabled={disabled}
                            displayEmpty
                            onChange={(e: SelectChangeEvent) =>
                                handleCellChange(rowIndex, column.key, e.target.value)
                            }
                            sx={{
                                '& .MuiSelect-select': { padding: '8px 12px' },
                                pointerEvents: readOnly ? 'none' : 'auto',
                            }}
                        >
                            {column.placeholder && (
                                <MenuItem value="" disabled>
                                    {column.placeholder}
                                </MenuItem>
                            )}
                            {column.selectOptions?.map((opt) => (
                                <MenuItem
                                    key={opt.selectItemKey || opt.selectItemValue}
                                    value={opt.selectItemValue}
                                >
                                    {opt.selectItemLabel}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'currency':
                return (
                    <CurrencyInput
                        name={`${name}_${rowIndex}_${column.key}`}
                        value={stringValue}
                        placeholder={column.placeholder}
                        disabled={disabled}
                        allowDecimal={true}
                        error={cellError}
                        onChange={(val) => handleCellChange(rowIndex, column.key, val.toString())}
                        sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                            pointerEvents: readOnly ? 'none' : 'auto',
                        }}
                    />
                );

            case 'percentage':
                return (
                    <PercentageInput
                        name={`${name}_${rowIndex}_${column.key}`}
                        value={stringValue}
                        placeholder={column.placeholder}
                        disabled={disabled}
                        allowDecimal={true}
                        error={cellError}
                        onChange={(val) => handleCellChange(rowIndex, column.key, val.toString())}
                        sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                            pointerEvents: readOnly ? 'none' : 'auto',
                        }}
                    />
                );

            case 'date': {
                const dateValue = stringValue
                    ? DateTime.fromJSDate(new Date(stringValue)).isValid
                        ? DateTime.fromJSDate(new Date(stringValue))
                        : null
                    : null;
                return (
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePicker
                            value={dateValue}
                            disabled={disabled}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    error: cellError,
                                    sx: {
                                        '& .MuiOutlinedInput-input': { padding: '8px 12px' },
                                        pointerEvents: readOnly ? 'none' : 'auto',
                                    },
                                },
                            }}
                            onChange={(newValue) => {
                                const isoString = newValue?.toISO() || '';
                                handleCellChange(rowIndex, column.key, isoString);
                            }}
                        />
                    </LocalizationProvider>
                );
            }

            case 'address':
                return (
                    <GooglePlacesAutocomplete
                        name={`${name}_${rowIndex}_${column.key}`}
                        value={stringValue}
                        disabled={disabled}
                        isError={cellError}
                        language={language}
                        onChange={(val) => handleCellChange(rowIndex, column.key, val)}
                    />
                );

            case 'upload': {
                const fileIds = Array.isArray(cellValue) ? cellValue : [];
                return (
                    <Box sx={{ minWidth: 120 }}>
                        <FormUploadField
                            name={`${name}_${rowIndex}_${column.key}`}
                            language={language}
                            uploadButtonLabel={fileUploadTranslations.upload}
                            fileIds={fileIds}
                            disabled={disabled}
                            isError={cellError}
                            selectedLawFirmId={selectedLawFirmId}
                            typeofDocument={column.typeOfDocument}
                            validationSchema={{
                                fileCount: column.fileCount ?? LEGACY_TABLE_UPLOAD_FILE_COUNT,
                                fileSize: 10 * 1024 * 1024,
                                fileType: [
                                    'application/pdf',
                                    'image/jpeg',
                                    'image/jpg',
                                    'image/png',
                                ],
                            }}
                            onFileIdsChange={(ids) => {
                                handleCellChange(rowIndex, column.key, ids);
                            }}
                        />
                    </Box>
                );
            }

            case 'multiselect': {
                const selectedValues = parseMultiSelectValue(stringValue);
                const handleToggle = (optionValue: string) => {
                    const nextSelected = sortMultiSelectByOptions(
                        selectedValues.includes(optionValue)
                            ? selectedValues.filter((v) => v !== optionValue)
                            : [...selectedValues, optionValue],
                        column.options,
                    );
                    handleCellChange(rowIndex, column.key, stringifyMultiSelectValue(nextSelected));
                };

                return (
                    <FormControl
                        error={cellError}
                        sx={{
                            minWidth: 160,
                            pointerEvents: readOnly ? 'none' : 'auto',
                        }}
                        fullWidth
                    >
                        {column.options?.map((option, index) => {
                            const optionValue = String(option.optionValue);
                            return (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={selectedValues.includes(optionValue)}
                                            disabled={disabled}
                                            sx={{
                                                color: cellError
                                                    ? theme.palette.error.main
                                                    : undefined,
                                                '&.Mui-checked': {
                                                    color: cellError
                                                        ? theme.palette.error.main
                                                        : undefined,
                                                },
                                            }}
                                            onChange={() => handleToggle(optionValue)}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            {option.optionLabel}
                                        </Typography>
                                    }
                                />
                            );
                        })}
                    </FormControl>
                );
            }

            default:
                return null;
        }
    };

    const renderMobileCard = (row: TableFormRow, rowIndex: number) => (
        <Box
            key={rowIndex}
            sx={{
                border: `1px solid ${isError ? theme.palette.error.main : theme.palette.neutral.N_200}`,
                borderRadius: '8px',
                padding: 2,
                marginBottom: 2,
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 2,
                }}
            >
                <Typography variant="body2" fontWeight="bold">
                    Entry {rowIndex + 1}
                </Typography>
                {canModifyRows && (
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteRow(rowIndex)}
                        disabled={disabled}
                        sx={{
                            padding: '4px',
                            color: theme.palette.grey[500],
                            '&:hover': {
                                color: theme.palette.error.main,
                            },
                        }}
                    >
                        <DeleteOutlineIcon />
                    </IconButton>
                )}
            </Box>
            {columns.map((column) => (
                <Box key={column.key} sx={{ marginBottom: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{ marginBottom: 0.5, color: theme.palette.text.secondary }}
                    >
                        {column.label}
                    </Typography>
                    {renderCell(column, rowIndex, row[column.key])}
                </Box>
            ))}
        </Box>
    );

    return (
        <Stack sx={{ width: '100%' }} spacing={1}>
            {isMobile ? (
                <Box>{rows.map((row, rowIndex) => renderMobileCard(row, rowIndex))}</Box>
            ) : (
                <TableContainer
                    sx={{
                        border: `1px solid ${isError ? theme.palette.error.main : theme.palette.neutral.N_200}`,
                        borderRadius: '4px',
                        mb: 1,
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.primary.P_50 }}>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            width: col.width,
                                            minWidth: col.type === 'address' ? 220 : undefined,
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                                {canModifyRows && <TableCell sx={{ width: 50, padding: '4px' }} />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, rowIndex) => (
                                <TableRow
                                    key={rowIndex}
                                    sx={{ backgroundColor: theme.palette.neutral.white }}
                                >
                                    {columns.map((col) => (
                                        <TableCell key={col.key} sx={{ padding: '8px' }}>
                                            {renderCell(col, rowIndex, row[col.key])}
                                        </TableCell>
                                    ))}
                                    {canModifyRows && (
                                        <TableCell sx={{ padding: '4px' }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteRow(rowIndex)}
                                                disabled={disabled}
                                                sx={{
                                                    padding: '4px',
                                                    color: theme.palette.grey[500],
                                                    '&:hover': {
                                                        color: theme.palette.error.main,
                                                    },
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {canModifyRows && (
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddRow}
                    disabled={disabled}
                    sx={{ textTransform: 'none', width: 'fit-content' }}
                >
                    {buttonTranslations.addRow}
                </Button>
            )}
        </Stack>
    );
}
